import React from 'react';
import { Helmet } from 'react-helmet';
import SEO_CONFIG from '../config/seo';

const defaultImage = SEO_CONFIG.defaultImage.startsWith('http')
  ? SEO_CONFIG.defaultImage
  : `${SEO_CONFIG.baseUrl}${SEO_CONFIG.defaultImage.startsWith('/') ? SEO_CONFIG.defaultImage : '/' + SEO_CONFIG.defaultImage}`;

/**
 * SEO component – sets document title, meta description, OG, Twitter, canonical, JSON-LD.
 * Use on each page for heavy SEO (Uber Eats, Mr D Food, restaurants, catering, etc.).
 */
export default function SEO({
  title,
  description,
  canonical,
  image = defaultImage,
  noindex = false,
  jsonLd = null,
  keywords = null,
}) {
  const asSafeString = (value, fallback = '') => {
    if (value == null) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value);
    return fallback;
  };

  const t = title ? `${asSafeString(title, SEO_CONFIG.defaultTitle)} | ${SEO_CONFIG.siteName}` : SEO_CONFIG.defaultTitle;
  const d = asSafeString(description, SEO_CONFIG.defaultDescription);
  // Normalize canonical path: no trailing slash (except for '/') to avoid duplicate-content issues
  const canonicalPath = typeof canonical === 'string' && canonical.startsWith('/')
    ? (canonical === '/' || canonical === '') ? '/' : canonical.replace(/\/+$/, '')
    : canonical;
  const c = canonicalPath
    ? (canonicalPath.startsWith('http') ? canonicalPath : `${SEO_CONFIG.baseUrl}${canonicalPath.startsWith('/') ? '' : '/'}${canonicalPath}`)
    : (typeof window !== 'undefined' ? window.location.href.replace(/\/+$/, '') || window.location.origin : SEO_CONFIG.baseUrl);
  const safeImage = asSafeString(image, defaultImage);
  const img = safeImage.startsWith('http') ? safeImage : `${SEO_CONFIG.baseUrl}${safeImage.startsWith('/') ? '' : '/'}${safeImage}`;
  const kw = Array.isArray(keywords)
    ? keywords.map((k) => asSafeString(k)).filter(Boolean).join(', ')
    : asSafeString(keywords, SEO_CONFIG.keywords);
  const jsonLdItems = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{t}</title>
      <meta name="description" content={d} />
      <meta name="keywords" content={kw} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {noindex && <meta name="googlebot" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}
      {!noindex && <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}
      <link rel="canonical" href={c} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={c} />
      <meta property="og:title" content={t} />
      <meta property="og:description" content={d} />
      <meta property="og:image" content={img} />
      <meta property="og:site_name" content={SEO_CONFIG.siteName} />
      <meta property="og:locale" content={SEO_CONFIG.locale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={c} />
      <meta name="twitter:title" content={t} />
      <meta name="twitter:description" content={d} />
      <meta name="twitter:image" content={img} />
      {SEO_CONFIG.twitterHandle && <meta name="twitter:site" content={SEO_CONFIG.twitterHandle} />}

      {/* JSON-LD */}
      {jsonLdItems.map((ld, i) => {
        let payload = '';
        try {
          payload = JSON.stringify(ld);
        } catch {
          payload = '';
        }
        return payload ? (
          <script key={i} type="application/ld+json">
            {payload}
          </script>
        ) : null;
      })}
    </Helmet>
  );
}

export { SEO_CONFIG };
